import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const AVATAR_FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%23e5e7eb'/%3E%3Ccircle cx='20' cy='15' r='7' fill='%239ca3af'/%3E%3Cellipse cx='20' cy='35' rx='12' ry='9' fill='%239ca3af'/%3E%3C/svg%3E";

const resolveAvatarUrl = (avatarUrl) => {
  if (!avatarUrl) return null;
  if (avatarUrl?.startsWith('http://') || avatarUrl?.startsWith('https://')) return avatarUrl;
  const { data } = supabase?.storage?.from('profile-avatars')?.getPublicUrl(avatarUrl);
  return data?.publicUrl || null;
};

const ListingCard = ({ listing, onContact, onWishlist, isWishlisted, isOwner, onMarkAsSold, onDelete }) => {
  const navigate = useNavigate();

  const getConditionColor = (condition) => {
    const colors = {
      'Sıfır': 'text-success bg-success/10',
      'Az Kullanılmış': 'text-primary bg-primary/10',
      'İyi Durumda': 'text-secondary bg-secondary/10',
      'Kullanılmış': 'text-warning bg-warning/10'
    };
    return colors?.[condition] || 'text-muted-foreground bg-muted';
  };

  const handleCardClick = () => {
    navigate(`/marketplace/${listing?.id}`);
  };

  const handleContactClick = (e) => {
    e?.stopPropagation();
    onContact(listing);
  };

  const handleWishlistClick = (e) => {
    e?.stopPropagation();
    onWishlist(listing?.id);
  };

  const sellerAvatarUrl = resolveAvatarUrl(listing?.sellerAvatar);

  return (
    <div 
      className="bg-card rounded-xl shadow-organic hover:shadow-organic-lg transition-smooth overflow-hidden"
      onClick={handleCardClick}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {listing?.image ? (
          <Image
            src={listing?.image}
            alt={listing?.imageAlt || `${listing?.title} görsel`}
            className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-muted text-muted-foreground gap-2">
            <Icon name="Camera" size={32} className="opacity-40" />
            <span className="text-xs opacity-60">Görsel yok</span>
          </div>
        )}
        
        {/* Sold Overlay */}
        {listing?.is_sold && (
          <div className="absolute inset-0 bg-black/60 z-[5] flex items-center justify-center pointer-events-none">
            <div className="bg-error text-error-foreground px-6 py-3 rounded-lg font-bold text-lg shadow-lg">
              SATILDI
            </div>
          </div>
        )}
        
        {/* Favorite Button */}
        <button
          onClick={handleWishlistClick}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-smooth z-[10] pointer-events-auto"
          aria-label={isWishlisted ? 'Favorilerden çıkar' : 'Favorilere ekle'}
        >
          <Icon 
            name={isWishlisted ? 'Heart' : 'Heart'} 
            size={18} 
            className={isWishlisted ? 'text-error fill-error' : 'text-foreground'}
          />
        </button>
        <div className="absolute top-3 left-3 z-[10]">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getConditionColor(listing?.condition)}`}>
            {listing?.condition}
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-base md:text-lg font-semibold text-foreground line-clamp-2 flex-1">
            {listing?.title}
          </h3>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {listing?.description}
        </p>

        <div className="flex items-center gap-2 mb-3">
          <Icon name="MapPin" size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground">{listing?.location}</span>
        </div>

        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
          <img
            src={sellerAvatarUrl || AVATAR_FALLBACK}
            alt={listing?.sellerAvatarAlt || listing?.sellerName || 'Satıcı'}
            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
            onError={(e) => { e.target.onerror = null; e.target.src = AVATAR_FALLBACK; }}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{listing?.sellerName}</p>
            <div className="flex items-center gap-1">
              <Icon name="Star" size={12} className="text-warning fill-warning" />
              <span className="text-xs text-muted-foreground">{listing?.sellerRating}</span>
              {listing?.sellerVerified && (
                <Icon name="BadgeCheck" size={12} className="text-primary ml-1" />
              )}
            </div>
          </div>
        </div>

        {isOwner && (
          <div className="mt-4 pt-4 border-t border-border space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="User" size={16} className="text-primary" />
              <span className="text-sm font-medium text-primary">Sizin İlanınız</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Check"
                iconPosition="left"
                onClick={onMarkAsSold}
                className="flex-1"
              >
                Satıldı İşaretle
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                onClick={onDelete}
                className="text-error hover:text-error hover:bg-error/10"
              >
                Sil
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-2xl font-bold text-primary">₺{listing?.price?.toLocaleString('tr-TR')}</p>
            <p className="text-xs text-muted-foreground">{listing?.postedDate}</p>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            iconSize={16}
            onClick={handleContactClick}
          >
            İletişim
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;